import type { Dispatch, SetStateAction } from 'react';
import { useLocalStorage } from 'usehooks-ts';

/**
 * Grid-card size preference — Settings → Appearance → "Icon sizes"
 * (Small = the DS `compact` 180 tile, Large = `expanded`). Applies to every
 * grid DeckCard / FolderCard / StudyGroupCard, their dialog previews
 * included. Stored client-side only (device preference, like the theme).
 */
export type CardSize = 'compact' | 'expanded';

const CARD_SIZE_KEY = 'card-size';

/**
 * Read + write the preference. Reactive across the app and across tabs.
 * Uses `usehooks-ts` directly rather than the app's `useLocalStorage`
 * wrapper: that module also exports auth/sync-backed user hooks, and every
 * DS card would then drag the auth stack into its render tree (and its tests).
 */
export function useCardSizeSetting(): [CardSize, Dispatch<SetStateAction<CardSize>>] {
  const [value, setValue] = useLocalStorage<CardSize>(CARD_SIZE_KEY, 'expanded');
  return [value, setValue];
}

/** Read-only accessor for the card call sites. */
export function useCardSize(): CardSize {
  return useCardSizeSetting()[0];
}

/** Rendered grid-tile box per size, in px — the DeckCard grid geometry. */
export const DECK_CARD_BOX: Record<CardSize, number> = { compact: 180, expanded: 225 };

/**
 * Width class every WEB grid card must carry — pass it as the DS card's
 * `className`.
 *
 * The shared compact tile is `w-full` (`cardVariants`), because MOBILE sizes
 * its cells with the tile engine and the card fills whatever column it lands
 * in. Web has no such engine: its grids are flex-wrap flows and its cards sit
 * in shrink-to-fit wrappers (inline-block `<Link>`s, dnd wrappers, centered
 * story canvases), where a percentage width collapses to min-content. So the
 * design box has to be stated at the call site — and it is stated HERE, once,
 * beside the {@link DECK_CARD_BOX} number it mirrors, rather than as a
 * hand-copied ternary at every render site (which is how the 11th call site
 * ships a collapsed card that nothing type-checks).
 *
 * `expanded` returns undefined: that variant carries its own inline box.
 */
export const compactCardClass = (size: CardSize): string | undefined =>
  size === 'compact' ? 'w-[180px]' : undefined;
